import { Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { TealiumAnalyticsService } from '@ddt-web/analytics';
import { ErrorDCR } from '@ddt-web/config';

import { PopupDialogAction } from '../models';
import { AbstractErrorHandlerService } from './abstract-error-handler.service';
import { ErrorHelperService } from './error-helper.service';
import { ErrorPopupService } from './error-popup.service';

/**
 * This is a commonly shared error handler.
 * Add it to the chain of handlers in app.module.ts like:
 * ErrorHandlerModule.forRoot({ useErrorHandlers: [STErrorHandlerService, MissingHoldingErrorHandlerService]})
 * If you want to customize, please add to application level handler
 * e.g. st-error-handler.service
 */
@Injectable()
export class PartnerErrorHandlerService extends AbstractErrorHandlerService {
	constructor(
		protected contentService: ContentService,
		protected errorPopupService: ErrorPopupService,
		protected tealiumAnalyticsService: TealiumAnalyticsService,
		protected errorHelperService: ErrorHelperService
	) {
		super(contentService, errorPopupService, tealiumAnalyticsService, errorHelperService);
	}

	public handleError(error): void {
		const isPartnerError =
			error.message &&
			(
				error.message.includes(ErrorDCR.PARTNER_NO_PORTFOLIO_ID_ERROR) ||
				error.message.includes(ErrorDCR.PARTNER_UNAUTHORISED_PORTFOLIO_ACCESS) ||
				error.message.includes(ErrorDCR.PARTNER_NO_PROGRAMS_TARGET_RISK_ERROR) ||
				error.message.includes(ErrorDCR.PARTNER_NO_MODEL_LIST_ERROR) ||
				error.message.includes(ErrorDCR.PARTNER_NO_DATA_FOUND_ERROR) ||
				error.message.includes(ErrorDCR.PARTNER_INVALID_TOKEN_ERROR)
			);

		const isPartnerDeeplinkError = (error.responseJson?.message && error.responseJson.message.includes(ErrorDCR.PARTNER_EXTERNAL_PORTFOLIO_DEEPLINK_REFRESH_ERROR)) ||
			(error.message && error.message.includes(ErrorDCR.PARTNER_EXTERNAL_PORTFOLIO_DEEPLINK_INVALID));

		if (isPartnerError) {
			const errorTitle = this.contentService.get('SOMETHING_WRONG_ERROR_TITLE', ErrorDCR.SOMETHING_WRONG_ERROR_TITLE);
			this.tealiumAnalyticsService.trackError(this.trackErrorMessage, this.errorSeverity);

			const actions = [
				PopupDialogAction.create({
					label: this.contentService.get('ERROR_CANCEL_LABEL', ErrorDCR.ERROR_CANCEL_LABEL),
					type: 'view_new_analysis',
					reload: false,
					actionClass: 'btn-secondary'
				}),
				PopupDialogAction.create({
					label: this.contentService.get('ERROR_RETRY_LABEL', ErrorDCR.ERROR_RETRY_LABEL),
					reload: true
				})
			];
			let errorMessage;

			if (error.message.includes(ErrorDCR.PARTNER_NO_PORTFOLIO_ID_ERROR)) {
				errorMessage = this.contentService.get('PARTNER_NO_PORTFOLIO_ID_ERROR', ErrorDCR.PARTNER_NO_PORTFOLIO_ID_ERROR);
				console.error(errorMessage, error);
				this.errorPopupService.openResponseMessageDialog(
					this.buildErrorMessage(false, 'error-partner-dialog', errorTitle, this.trackErrorMessage, errorMessage, true, [], actions)
				);
			}

			if (error.message.includes(ErrorDCR.PARTNER_UNAUTHORISED_PORTFOLIO_ACCESS)) {
				errorMessage = this.contentService.get('PARTNER_UNAUTHORISED_PORTFOLIO_ACCESS', ErrorDCR.PARTNER_UNAUTHORISED_PORTFOLIO_ACCESS);
				console.error(errorMessage, error);
				this.errorPopupService.openResponseMessageDialog(
					this.buildErrorMessage(false, 'error-partner-dialog', errorTitle, this.trackErrorMessage, errorMessage, true, [], actions)
				);
			}

			if (error.message.includes(ErrorDCR.PARTNER_NO_PROGRAMS_TARGET_RISK_ERROR)) {
				errorMessage = this.contentService.get('PARTNER_NO_PROGRAMS_TARGET_RISK_ERROR', ErrorDCR.PARTNER_NO_PROGRAMS_TARGET_RISK_ERROR);
				console.error(errorMessage, error);

				// currently reloading the page to make the api call that failed again; TODO: refactor logic
				const actions = [
					PopupDialogAction.create({
						label: this.contentService.get('ERROR_CANCEL_LABEL', ErrorDCR.ERROR_CANCEL_LABEL),
						reload: true
					})
				];
				this.errorPopupService.openResponseMessageDialog(
					this.buildErrorMessage(false, 'error-partner-dialog', errorTitle, this.trackErrorMessage, errorMessage, true, [], actions)
				);
			}

			if (error.message.includes(ErrorDCR.PARTNER_NO_MODEL_LIST_ERROR)) {
				errorMessage = this.contentService.get('PARTNER_NO_MODEL_LIST_ERROR', ErrorDCR.PARTNER_NO_MODEL_LIST_ERROR);
				console.error(errorMessage, error);
				this.errorPopupService.openResponseMessageDialog(
					this.buildErrorMessage(false, 'error-partner-dialog', errorTitle, this.trackErrorMessage, errorMessage, true, [], actions)
				);
			}

			if (error.message.includes(ErrorDCR.PARTNER_INVALID_TOKEN_ERROR)) {
				const errorMessage = this.contentService.get('PARTNER_INVALID_TOKEN_ERROR', ErrorDCR.PARTNER_INVALID_TOKEN_ERROR);
				console.error(errorMessage, error);
				const actions = [
					PopupDialogAction.create({
						label: this.contentService.get('ERROR_CONTINUE_LABEL', 'Continue'),
						type: 'continue',
						reload: false
					}),
					PopupDialogAction.create({
						label: this.contentService.get('ERROR_GO_TO_PARTNER_LABEL', ErrorDCR.ERROR_GO_TO_PARTNER_LABEL),
						type: 'resolve',
						unresolvedUrl: 'envestnetUrl',
						reload: false
					})
				];
				const errorTitle = this.contentService.get('SOMETHING_WRONG_ERROR_TITLE', ErrorDCR.SOMETHING_WRONG_ERROR_TITLE);

				this.errorPopupService.openResponseMessageDialog(
					this.buildErrorMessage(false, 'error-partner-dialog', errorTitle, this.trackErrorMessage, errorMessage, true, [], actions)
				);
			}

			if (error.message.includes(ErrorDCR.PARTNER_NO_DATA_FOUND_ERROR)) {
				errorMessage = this.contentService.get('PARTNER_GENERIC_MESSAGE_ERROR', ErrorDCR.PARTNER_GENERIC_MESSAGE_ERROR);
				console.error(errorMessage, error);
				this.errorPopupService.openResponseMessageDialog(
					this.buildErrorMessage(false, 'error-partner-dialog', errorTitle, this.trackErrorMessage, errorMessage, true, [], actions)
				);
			}

		} else if (isPartnerDeeplinkError) {
			const errorTitle = this.contentService.get('SOMETHING_WRONG_ERROR_TITLE', ErrorDCR.SOMETHING_WRONG_ERROR_TITLE);
			this.tealiumAnalyticsService.trackError(this.trackErrorMessage, this.errorSeverity);

			const requestId = error?.responseJson?.requestId;
			const errorMsg = error?.responseJson?.message || error?.message;
			const dcrKey = errorMsg.includes(ErrorDCR.PARTNER_EXTERNAL_PORTFOLIO_DEEPLINK_INVALID)
				? 'PARTNER_EXTERNAL_PORTFOLIO_DEEPLINK_INVALID' : 'PARTNER_EXTERNAL_PORTFOLIO_DEEPLINK_REFRESH_ERROR';
			const actions = [
				PopupDialogAction.create({
					label: this.contentService.get('ERROR_NEW_ANALYSIS_LABEL', ErrorDCR.ERROR_NEW_ANALYSIS_LABEL),
					type: 'view_new_analysis',
					reload: false
				})
			];
			const errorMessage = this.contentService.get(dcrKey, ErrorDCR[dcrKey]);
			console.error(errorMessage, error);
			this.errorPopupService.openResponseMessageDialog(
				this.buildErrorMessage(false, 'error-partner-dialog', errorTitle, this.trackErrorMessage, errorMessage, true, [], actions, requestId)
			);

		} else {
			this.nextHandler.handleError(error);
		}
	}
}
